import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CalendarDots, GameController, Trophy, Users, UserCircle, Medal,
  House, ChartBar, CrownSimple, Plus, Trash, Check, Clock, Lightning,
  Pencil, Eye, UserPlus, X, ArrowRight, Star, Crown, Storefront, Rocket
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = (cookie = true) => axios.create({ baseURL: API_URL, withCredentials: cookie });

const StatCard = ({ icon: Icon, label, value, accent = '#00F0FF' }) => (
  <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} weight="duotone" style={{ color: accent }} />
      <span className="text-xs text-[#666] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

export function ShopOverview() {
  const { shopId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { ax().get(`/api/shop-portal/${shopId}/overview`).then(r => setData(r.data)).catch(() => {}); }, [shopId]);
  if (!data) return <div className="text-[#777]">Loading...</div>;
  return (
    <div data-testid="portal-overview">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={CalendarDots} label="Upcoming Events" value={data.upcoming_events} />
        <StatCard icon={GameController} label="Active Open Play" value={data.active_open_play} accent="#9D4CDD" />
        <StatCard icon={Trophy} label="Tournaments" value={data.active_tournaments} accent="#F59E0B" />
        <StatCard icon={Users} label="RSVPs This Week" value={data.rsvps_this_week} accent="#10B981" />
        <StatCard icon={UserCircle} label="Members" value={data.member_count} accent="#3B82F6" />
        <StatCard icon={Medal} label="Badges" value={data.badge_count} accent="#EC4899" />
      </div>
      {data.champion_name && (
        <div className="bg-[#0e0e16] border border-[#F59E0B]/30 rounded-lg p-4 flex items-center gap-3">
          <CrownSimple size={24} weight="duotone" className="text-[#F59E0B]" />
          <div><p className="text-xs text-[#F59E0B] uppercase tracking-wider">Shop Champion</p><p className="text-white font-semibold">{data.champion_name}</p></div>
        </div>
      )}
    </div>
  );
}

export function ShopEvents() {
  const { shopId } = useParams();
  const [events, setEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', event_type: 'game_night', game_name: '', scheduled_start: '', max_players: 16, beginner_friendly: true, skill_level: 'all' });
  const fetchEv = useCallback(() => { ax().get(`/api/shop-portal/${shopId}/events`).then(r => setEvents(r.data)); }, [shopId]);
  useEffect(() => { fetchEv(); }, [fetchEv]);
  const create = async () => {
    if (!form.title || !form.scheduled_start) { toast.error('Title and start time required'); return; }
    try { await ax().post(`/api/shop-portal/${shopId}/events`, form); toast.success('Event created!'); setShowCreate(false); fetchEv(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete?')) return; await ax().delete(`/api/shop-portal/${shopId}/events/${id}`); toast.success('Deleted'); fetchEv(); };
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D4CDD]/50 focus:outline-none";
  const EVENT_TYPES = ['game_night', 'learn_to_play', 'open_table', 'league_night', 'themed_night', 'demo_night', 'free_play'];
  return (
    <div data-testid="portal-events">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Events</h2>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm hover:bg-[#8B3DC7] transition-colors" data-testid="create-event-btn">
          <Plus size={16} weight="bold" /> New Event
        </button>
      </div>
      {showCreate && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 mb-6 space-y-3">
          <input className={inputCls} placeholder="Event title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" />
          <textarea className={inputCls + " h-16 resize-none"} placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <select className={inputCls} value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })}>{EVENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}</select>
            <input className={inputCls} placeholder="Game name" value={form.game_name} onChange={e => setForm({ ...form, game_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} type="datetime-local" value={form.scheduled_start} onChange={e => setForm({ ...form, scheduled_start: e.target.value })} data-testid="event-datetime-input" />
            <input className={inputCls} type="number" placeholder="Max players" value={form.max_players} onChange={e => setForm({ ...form, max_players: parseInt(e.target.value) || 16 })} />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={create} className="px-4 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm" data-testid="submit-event-btn">Create</button>
          </div>
        </motion.div>
      )}
      <div className="space-y-3">
        {events.length === 0 && <p className="text-[#666] text-sm">No events yet. Create your first one!</p>}
        {events.map(e => (
          <div key={e.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#9D4CDD]/10 text-[#9D4CDD]">{(e.event_type || '').replace(/_/g, ' ')}</span>
              </div>
              <p className="text-white font-medium truncate">{e.title}</p>
              <div className="flex items-center gap-3 text-xs text-[#777] mt-1">
                {e.game_name && <span>{e.game_name}</span>}
                <span><Clock size={12} className="inline" /> {e.scheduled_start ? new Date(e.scheduled_start).toLocaleDateString() : e.date || ''}</span>
                <span><Users size={12} className="inline" /> {e.rsvp_going || 0} going</span>
              </div>
            </div>
            <button onClick={() => del(e.id)} className="text-[#555] hover:text-red-400 ml-3"><Trash size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShopOpenPlay() {
  const { shopId } = useParams();
  const [posts, setPosts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', game_name: '', scheduled_start: '', seats_available: 4, skill_level: 'all' });
  const fetchP = useCallback(() => { ax().get(`/api/shop-portal/${shopId}/open-play`).then(r => setPosts(r.data)); }, [shopId]);
  useEffect(() => { fetchP(); }, [fetchP]);
  const create = async () => {
    if (!form.title || !form.scheduled_start) { toast.error('Title and time required'); return; }
    try { await ax().post(`/api/shop-portal/${shopId}/open-play`, form); toast.success('Posted!'); setShowCreate(false); fetchP(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { await ax().delete(`/api/shop-portal/${shopId}/open-play/${id}`); toast.success('Deleted'); fetchP(); };
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D4CDD]/50 focus:outline-none";
  return (
    <div data-testid="portal-open-play">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Open Play</h2>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm" data-testid="create-openplay-btn"><Plus size={16} weight="bold" /> Post Table</button>
      </div>
      {showCreate && (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 mb-6 space-y-3">
          <input className={inputCls} placeholder="e.g. Open tables tonight!" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="openplay-title-input" />
          <div className="grid grid-cols-3 gap-3">
            <input className={inputCls} placeholder="Game" value={form.game_name} onChange={e => setForm({ ...form, game_name: e.target.value })} />
            <input className={inputCls} type="datetime-local" value={form.scheduled_start} onChange={e => setForm({ ...form, scheduled_start: e.target.value })} data-testid="openplay-datetime-input" />
            <input className={inputCls} type="number" placeholder="Seats" value={form.seats_available} onChange={e => setForm({ ...form, seats_available: parseInt(e.target.value) || 4 })} />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={create} className="px-4 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm" data-testid="submit-openplay-btn">Post</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {posts.length === 0 && <p className="text-[#666] text-sm">No open play posts yet.</p>}
        {posts.map(p => (
          <div key={p.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-white font-medium">{p.title}</p>
              <div className="flex items-center gap-3 text-xs text-[#777] mt-1">
                {p.game_name && <span>{p.game_name}</span>}
                <span><Clock size={12} className="inline" /> {p.scheduled_start ? new Date(p.scheduled_start).toLocaleDateString() : ''}</span>
                <span className="text-[#9D4CDD]">{Math.max(0, (p.seats_available || 0) - (p.seats_taken || 0))} seats open</span>
              </div>
            </div>
            <button onClick={() => del(p.id)} className="text-[#555] hover:text-red-400 ml-3"><Trash size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShopTournaments() {
  const { shopId } = useParams();
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', game_type: 'Catan', description: '', date: '', time: '13:00', max_players: 8 });
  const fetchT = useCallback(() => { ax().get(`/api/shop-portal/${shopId}/tournaments`).then(r => setList(r.data)); }, [shopId]);
  useEffect(() => { fetchT(); }, [fetchT]);
  const create = async () => {
    if (!form.name || !form.date) { toast.error('Name and date required'); return; }
    try { await ax().post(`/api/shop-portal/${shopId}/tournaments`, form); toast.success('Created!'); setShowCreate(false); fetchT(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const updateStatus = async (id, status) => { await ax().put(`/api/shop-portal/${shopId}/tournaments/${id}/status`, { status }); toast.success(`Status: ${status}`); fetchT(); };
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#F59E0B]/50 focus:outline-none";
  const statusColors = { registration: '#00F0FF', in_progress: '#F59E0B', completed: '#10B981', cancelled: '#EF4444' };
  return (
    <div data-testid="portal-tournaments">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Tournaments</h2>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm" data-testid="create-tournament-btn"><Plus size={16} weight="bold" /> New</button>
      </div>
      {showCreate && (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 mb-6 space-y-3">
          <input className={inputCls} placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="tournament-name-input" />
          <div className="grid grid-cols-3 gap-3">
            <input className={inputCls} type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="tournament-date-input" />
            <input className={inputCls} type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} />
            <input className={inputCls} type="number" placeholder="Max" value={form.max_players} onChange={e => setForm({ ...form, max_players: parseInt(e.target.value) || 8 })} />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={create} className="px-4 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm" data-testid="submit-tournament-btn">Create</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {list.length === 0 && <p className="text-[#666] text-sm">No tournaments yet.</p>}
        {list.map(t => (
          <div key={t.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white font-medium">{t.name}</p>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${statusColors[t.status] || '#666'}20`, color: statusColors[t.status] || '#666' }}>{t.status}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-[#777] mb-3">
              <span>{t.game_type}</span><span><Clock size={12} className="inline" /> {t.date}</span><span><Users size={12} className="inline" /> {t.current_players || 0}/{t.max_players}</span>
            </div>
            <div className="flex gap-2">
              {t.status === 'registration' && <button onClick={() => updateStatus(t.id, 'in_progress')} className="text-xs px-3 py-1 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20">Start</button>}
              {t.status === 'in_progress' && <button onClick={() => updateStatus(t.id, 'completed')} className="text-xs px-3 py-1 rounded-lg bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20">Complete</button>}
              {!['cancelled', 'completed'].includes(t.status) && <button onClick={() => updateStatus(t.id, 'cancelled')} className="text-xs px-3 py-1 rounded-lg bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20">Cancel</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShopAttendees() {
  const { shopId } = useParams();
  const [data, setData] = useState([]);
  useEffect(() => { ax().get(`/api/shop-portal/${shopId}/attendees`).then(r => setData(r.data)).catch(() => {}); }, [shopId]);
  const checkIn = async (eventId, userId) => {
    await ax().put(`/api/shop-portal/${shopId}/attendees/${eventId}/${userId}/check-in`);
    toast.success('Checked in!');
    ax().get(`/api/shop-portal/${shopId}/attendees`).then(r => setData(r.data));
  };
  return (
    <div data-testid="portal-attendees">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Attendees</h2>
      {data.length === 0 && <p className="text-[#666] text-sm">No upcoming events with attendees.</p>}
      {data.map(evt => (
        <div key={evt.event_id} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div><p className="text-white font-medium">{evt.title}</p><p className="text-xs text-[#777]">{evt.scheduled_start ? new Date(evt.scheduled_start).toLocaleDateString() : ''}</p></div>
            <div className="text-right text-xs"><p className="text-[#00F0FF]">{evt.going} going</p><p className="text-[#10B981]">{evt.checked_in} checked in</p></div>
          </div>
          <div className="space-y-2">
            {evt.attendees.map(a => (
              <div key={a.user_id} className="flex items-center justify-between py-2 border-t border-white/[0.04]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center text-xs text-[#9D4CDD] font-semibold">{(a.name || '?')[0]}</div>
                  <div><p className="text-sm text-white">{a.name}</p><p className="text-[10px] text-[#555]">Lv.{a.level}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-md ${a.status === 'checked_in' ? 'bg-[#10B981]/10 text-[#10B981]' : a.status === 'going' ? 'bg-[#00F0FF]/10 text-[#00F0FF]' : 'bg-white/5 text-[#777]'}`}>{a.status}</span>
                  {a.status === 'going' && <button onClick={() => checkIn(evt.event_id, a.user_id)} className="text-xs px-2 py-0.5 rounded-md bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20"><Check size={12} className="inline" /> In</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ShopProfile() {
  const { shopId } = useParams();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { ax().get(`/api/shop-portal/${shopId}/profile`).then(r => { setProfile(r.data); setForm(r.data); }).catch(() => {}); }, [shopId]);
  const save = async () => {
    const updates = {};
    ['name', 'description', 'email', 'phone', 'website_url', 'city', 'state'].forEach(k => { if (form[k] !== profile[k]) updates[k] = form[k]; });
    if (!Object.keys(updates).length) { setEditing(false); return; }
    try { await ax().put(`/api/shop-portal/${shopId}/profile`, updates); toast.success('Updated'); setProfile({ ...profile, ...updates }); setEditing(false); } catch { toast.error('Failed'); }
  };
  if (!profile) return <div className="text-[#777]">Loading...</div>;
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D4CDD]/50 focus:outline-none";
  return (
    <div data-testid="portal-profile">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Shop Profile</h2>
        {!editing ? <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/[0.08] text-[#888] text-sm hover:text-white"><Pencil size={16} /> Edit</button>
          : <button onClick={save} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold"><Check size={16} /> Save</button>}
      </div>
      <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-xs text-[#666] mb-1 block">Name</label>{editing ? <input className={inputCls} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} /> : <p className="text-white">{profile.name}</p>}</div>
          <div><label className="text-xs text-[#666] mb-1 block">Email</label>{editing ? <input className={inputCls} value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /> : <p className="text-white">{profile.email || '-'}</p>}</div>
          <div><label className="text-xs text-[#666] mb-1 block">City</label>{editing ? <input className={inputCls} value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} /> : <p className="text-white">{profile.city || '-'}</p>}</div>
          <div><label className="text-xs text-[#666] mb-1 block">Website</label>{editing ? <input className={inputCls} value={form.website_url || ''} onChange={e => setForm({ ...form, website_url: e.target.value })} /> : <p className="text-white">{profile.website_url || '-'}</p>}</div>
        </div>
        <div><label className="text-xs text-[#666] mb-1 block">Description</label>{editing ? <textarea className={inputCls + " h-20 resize-none"} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /> : <p className="text-white text-sm">{profile.description || '-'}</p>}</div>
      </div>
    </div>
  );
}

export function ShopBadges() {
  const { shopId } = useParams();
  const [badges, setBadges] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', icon: 'trophy', condition: 'tournament_win' });
  const fetchB = useCallback(() => { ax().get(`/api/shop-portal/${shopId}/badges`).then(r => setBadges(r.data)); }, [shopId]);
  useEffect(() => { fetchB(); }, [fetchB]);
  const create = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try { await ax().post(`/api/shop-portal/${shopId}/badges`, form); toast.success('Created!'); setShowCreate(false); fetchB(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#EC4899]/50 focus:outline-none";
  return (
    <div data-testid="portal-badges">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Badges</h2>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#EC4899] text-white font-semibold text-sm"><Plus size={16} weight="bold" /> New</button>
      </div>
      {showCreate && (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 mb-6 space-y-3">
          <input className={inputCls} placeholder="Badge name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input className={inputCls} placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={create} className="px-4 py-2 rounded-lg bg-[#EC4899] text-white font-semibold text-sm">Create</button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {badges.length === 0 && <p className="text-[#666] text-sm col-span-2">No badges yet.</p>}
        {badges.map(b => (
          <div key={b.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 flex items-center gap-3">
            <Medal size={28} weight="duotone" className="text-[#EC4899]" />
            <div className="flex-1 min-w-0"><p className="text-white font-medium">{b.name}</p><p className="text-xs text-[#777]">{b.description}</p></div>
            <span className="text-xs text-[#EC4899] bg-[#EC4899]/10 px-2 py-0.5 rounded-full">{b.awarded_count || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShopManagers() {
  const { shopId } = useParams();
  const [managers, setManagers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: '', role: 'event_coordinator' });
  const fetchM = useCallback(() => { ax().get(`/api/shop-portal/${shopId}/managers`).then(r => setManagers(r.data)).catch(() => {}); }, [shopId]);
  useEffect(() => { fetchM(); }, [fetchM]);
  const invite = async () => {
    if (!form.email) { toast.error('Email required'); return; }
    try { await ax().post(`/api/shop-portal/${shopId}/managers/invite`, form); toast.success('Invited!'); setShowInvite(false); fetchM(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Remove?')) return; await ax().delete(`/api/shop-portal/${shopId}/managers/${id}`); toast.success('Removed'); fetchM(); };
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D4CDD]/50 focus:outline-none";
  const roleColors = { owner: '#F59E0B', manager: '#00F0FF', event_coordinator: '#10B981', tournament_coordinator: '#9D4CDD' };
  return (
    <div data-testid="portal-managers">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Managers</h2>
        <button onClick={() => setShowInvite(!showInvite)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/[0.08] text-[#888] text-sm hover:text-white"><UserPlus size={16} /> Invite</button>
      </div>
      {showInvite && (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 mb-6 space-y-3">
          <input className={inputCls} placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
          <select className={inputCls} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="manager">Manager</option><option value="event_coordinator">Event Coordinator</option><option value="tournament_coordinator">Tournament Coordinator</option>
          </select>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={invite} className="px-4 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm" data-testid="submit-invite-btn">Invite</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {managers.map(m => (
          <div key={m.user_id} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-sm text-[#9D4CDD] font-semibold">{(m.name || '?')[0]}</div>
              <div><p className="text-white text-sm font-medium">{m.name}</p><p className="text-[11px] text-[#555]">{m.email}</p></div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] px-2 py-0.5 rounded-md" style={{ backgroundColor: `${roleColors[m.role] || '#666'}15`, color: roleColors[m.role] || '#666' }}>{m.role}</span>
              {m.role !== 'owner' && m.id && <button onClick={() => remove(m.id)} className="text-[#555] hover:text-red-400"><Trash size={16} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShopInsights() {
  const { shopId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { ax().get(`/api/shop-portal/${shopId}/analytics`).then(r => setData(r.data)).catch(() => {}); }, [shopId]);
  if (!data) return <div className="text-[#777]">Loading...</div>;
  return (
    <div data-testid="portal-insights">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Insights</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={CalendarDots} label="Events (30d)" value={data.month_events} />
        <StatCard icon={Users} label="Total RSVPs" value={data.total_rsvps} accent="#10B981" />
        <StatCard icon={ChartBar} label="Fill Rate" value={`${data.seat_fill_rate}%`} accent="#9D4CDD" />
        <StatCard icon={Trophy} label="Tournaments" value={data.total_tournaments} accent="#F59E0B" />
        <StatCard icon={UserCircle} label="Members" value={data.member_count} accent="#EC4899" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
          <p className="text-sm font-semibold text-white mb-3">Popular Games</p>
          {data.popular_games?.map((g, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
              <span className="text-sm text-white">{g.game}</span>
              <span className="text-xs text-[#9D4CDD]">{g.count} events</span>
            </div>
          ))}
          {(!data.popular_games || data.popular_games.length === 0) && <p className="text-xs text-[#555]">No data</p>}
        </div>
      </div>
    </div>
  );
}

const SHOP_PLAN_ICONS = { shop_free: Storefront, shop_growth: Rocket, shop_pro: Crown };
const SHOP_PLAN_ACCENTS = { shop_free: '#888', shop_growth: '#9D4CDD', shop_pro: '#F59E0B' };
const SHOP_FEATURE_LABELS = {
  'shop.events.active_limit': 'Active Events',
  'shop.open_play.active_limit': 'Open Play Posts',
  'shop.tournaments.advanced': 'Advanced Tournaments',
  'shop.recurring_events': 'Recurring Events',
  'shop.manager_seats': 'Manager Seats',
  'shop.analytics': 'Shop Analytics',
  'shop.discovery.boost': 'Discovery Boost',
  'shop.badges.champion': 'Champion/Badge Tools',
};

function fmtEnt(ent) {
  if (!ent) return { text: '\u2014', enabled: false };
  if (ent.type === 'boolean') return { text: ent.value === 'true' ? 'Included' : '\u2014', enabled: ent.value === 'true' };
  if (ent.type === 'limit') {
    const v = parseInt(ent.value);
    if (v >= 9999) return { text: 'Unlimited', enabled: true };
    return { text: `${v}`, enabled: true };
  }
  return { text: ent.value, enabled: true };
}

export function ShopPlan() {
  const { shopId } = useParams();
  const [comparison, setComparison] = useState(null);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      ax().get('/api/monetization/plans/compare?tenant_type=shop'),
      ax().get(`/api/monetization/shop/${shopId}/subscription`),
    ]).then(([cmp, sub]) => {
      setComparison(cmp.data);
      setCurrentSub(sub.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const switchPlan = async (planCode) => {
    setSubscribing(true);
    try {
      await ax().post(`/api/monetization/shop/${shopId}/subscribe`, { plan_code: planCode });
      toast.success(planCode === 'shop_free' ? 'Downgraded to Free' : 'Plan updated!');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to switch plan'); }
    finally { setSubscribing(false); }
  };

  if (loading) return (
    <div data-testid="portal-plan">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Shop Plan</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => <div key={i} className="h-80 bg-white/[0.03] rounded-xl animate-pulse" />)}
      </div>
    </div>
  );

  const plans = comparison?.plans || [];
  const features = comparison?.features || [];
  const currentPlanCode = currentSub?.plan_code || 'shop_free';

  return (
    <div data-testid="portal-plan">
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Shop Plan & Billing</h2>
        <p className="text-[13px] text-[#555] mt-1">Manage your shop subscription and features</p>
      </div>

      {/* Current Plan Summary */}
      <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-5 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${SHOP_PLAN_ACCENTS[currentPlanCode] || '#888'}15` }}>
              {React.createElement(SHOP_PLAN_ICONS[currentPlanCode] || Storefront, {
                size: 20, weight: 'duotone', style: { color: SHOP_PLAN_ACCENTS[currentPlanCode] || '#888' }
              })}
            </div>
            <div>
              <p className="text-white font-bold text-[15px]">{currentSub?.plan_name || 'Free'}</p>
              <p className="text-[11px] text-[#555]">
                {currentSub?.monthly_price > 0 ? `$${currentSub.monthly_price}/mo` : 'Free forever'}
                {currentSub?.status && currentSub.status !== 'none' && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] bg-[#10B981]/10 text-[#10B981] font-semibold uppercase">{currentSub.status}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Comparison Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {plans.map(plan => {
          const Icon = SHOP_PLAN_ICONS[plan.plan_code] || Storefront;
          const accent = SHOP_PLAN_ACCENTS[plan.plan_code] || '#888';
          const isCurrent = currentPlanCode === plan.plan_code;
          const isPopular = plan.plan_code === 'shop_growth';

          return (
            <div key={plan.plan_code}
              className={`relative rounded-xl border p-5 flex flex-col transition-all ${isCurrent ? 'bg-[#0c0c14]' : 'bg-[#0a0a12] hover:border-white/[0.12]'}`}
              style={isCurrent ? { borderColor: `${accent}40`, backgroundColor: `${accent}05` } : { borderColor: 'rgba(255,255,255,0.04)' }}
              data-testid={`shop-plan-card-${plan.plan_code}`}>

              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: accent, color: '#000' }}>Best Value</div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#10B981] text-white">Current</div>
              )}

              <div className="flex items-center gap-2 mb-3 mt-1">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
                  <Icon size={18} weight="duotone" style={{ color: accent }} />
                </div>
                <div>
                  <p className="text-white font-bold text-[15px]">{plan.name}</p>
                  <p className="text-[10px] text-[#444]">{plan.description}</p>
                </div>
              </div>

              <div className="mb-4">
                {plan.monthly_price === 0 ? (
                  <p className="text-2xl font-bold text-white">Free</p>
                ) : (
                  <p className="text-2xl font-bold text-white">
                    ${plan.monthly_price}<span className="text-[13px] text-[#555] font-normal">/mo</span>
                  </p>
                )}
              </div>

              <div className="flex-1 space-y-2 mb-5">
                {features.map(f => {
                  const ent = plan.entitlements[f.feature_code];
                  const fmt = fmtEnt(ent);
                  return (
                    <div key={f.feature_code} className="flex items-center gap-2">
                      {fmt.enabled ? <Check size={14} weight="bold" style={{ color: accent }} /> : <X size={14} className="text-[#2a2a2a]" />}
                      <span className={`text-[12px] ${fmt.enabled ? 'text-[#999]' : 'text-[#333]'}`}>
                        {SHOP_FEATURE_LABELS[f.feature_code] || f.feature_name}
                        {fmt.enabled && fmt.text !== 'Included' && (
                          <span className="text-[10px] ml-1" style={{ color: `${accent}aa` }}>({fmt.text})</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => switchPlan(plan.plan_code)}
                disabled={isCurrent || subscribing}
                className={`w-full py-2.5 rounded-lg text-[13px] font-semibold transition-all flex items-center justify-center gap-2 ${isCurrent ? 'bg-white/5 text-[#444] cursor-default' : 'text-white hover:opacity-90'}`}
                style={!isCurrent ? { backgroundColor: accent } : {}}
                data-testid={`shop-subscribe-${plan.plan_code}`}>
                {isCurrent ? 'Current Plan' : subscribing ? 'Processing...' : plan.monthly_price === 0 ? 'Downgrade' : plan.monthly_price > (currentSub?.monthly_price || 0) ? 'Upgrade' : 'Switch'}
                {!isCurrent && <ArrowRight size={14} />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export function ShopCommunity() {
  const { shopId } = useParams();
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [leagues, setLeagues] = useState([]);

  useEffect(() => {
    Promise.all([
      ax().get(`/api/shop-portal/${shopId}/community-stats`),
      ax().get(`/api/shop-portal/${shopId}/recurring-groups`),
      ax().get(`/api/shop-portal/${shopId}/leagues`),
    ]).then(([sRes, gRes, lRes]) => {
      setStats(sRes.data);
      setGroups(gRes.data);
      setLeagues(lRes.data);
    }).catch(() => {});
  }, [shopId]);

  return (
    <div data-testid="portal-community">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Community</h2>

      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard icon={Users} label="Groups" value={stats.recurring_groups} accent="#9D4CDD" />
          <StatCard icon={Trophy} label="Leagues" value={stats.leagues} accent="#F59E0B" />
          <StatCard icon={Users} label="Community Members" value={stats.total_community_members} accent="#10B981" />
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Users size={16} weight="duotone" className="text-[#9D4CDD]" /> Recurring Groups
        </h3>
        {groups.length === 0 ? (
          <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 text-center">
            <p className="text-[#555] text-sm">No recurring groups yet</p>
            <p className="text-[#333] text-xs mt-1">Create a group via the Groups page with this shop's ID</p>
          </div>
        ) : (
          <div className="space-y-2">
            {groups.map(g => (
              <div key={g.id} className="flex items-center gap-3 bg-[#0e0e16] border border-white/[0.06] rounded-lg px-4 py-3" data-testid={`shop-group-${g.id}`}>
                <Users size={16} weight="duotone" className="text-[#9D4CDD]" />
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{g.name}</p>
                  <p className="text-xs text-[#555]">{g.group_type} | {g.member_count} members</p>
                </div>
                <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full ${g.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-white/5 text-[#555]'}`}>{g.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Trophy size={16} weight="duotone" className="text-[#F59E0B]" /> Leagues
        </h3>
        {leagues.length === 0 ? (
          <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4 text-center">
            <p className="text-[#555] text-sm">No leagues yet</p>
            <p className="text-[#333] text-xs mt-1">Create a league via the Leagues page with this shop's ID</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leagues.map(lg => (
              <div key={lg.id} className="flex items-center gap-3 bg-[#0e0e16] border border-white/[0.06] rounded-lg px-4 py-3" data-testid={`shop-league-${lg.id}`}>
                <Trophy size={16} weight="duotone" className="text-[#F59E0B]" />
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{lg.name}</p>
                  <p className="text-xs text-[#555]">{lg.game_id} | {lg.league_type} | {lg.member_count} members</p>
                </div>
                {lg.active_season_name && <span className="text-[10px] text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded-full">{lg.active_season_name}</span>}
                <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full ${lg.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-white/5 text-[#555]'}`}>{lg.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
